// The Lately vault: one DEK per browser profile, unwrapped either silently by
// the browser-guarded session key SK (0 taps) or by a lately.org passkey's
// PRF output (1 Touch/Face ID). See docs/runbooks/lately-shared-auth.md.
//
// Boot policy ("unlock once per boot", as far as a browser allows):
//   SK present            -> silent unlock, no prompt
//   SK absent, slots      -> passkey ceremony (needs a user gesture; the UI
//                            shows an Unlock button and calls unlock({interactive:true}))
//   SK absent, no slots   -> nothing can unwrap the DEK: the vault is reset
//                            and the user signs in again (all local state is a
//                            cache of the server, so nothing is lost)
//   no header             -> fresh profile: create() at first sign-in
// SK rotates every 30 days: with a passkey enrolled that means one prompt;
// without one the SK is re-minted silently (there is nothing stronger to ask
// for). Browsers expose no boot signal, so "once per boot" is "once per
// browser profile until Lock / sign-out / storage clear / rotation" — the UI
// says so.
//
// Browsers without PRF (Firefox < 135, older Safari) run SK-only ("unlocked by
// this browser"): today's behaviour, no passkey.
//
// One passkey, every surface. Passkeys sync through the platform password
// manager (iCloud Keychain / Google PM / 1Password) and PRF gives the same
// KEK on every device that holds the credential, so enrolment is reuse-first:
// a discoverable assertion (allowCredentials = []) asks whichever lately.org
// passkey exists to evaluate the PRF and, if one answers, this vault's DEK is
// wrapped under that KEK — a new slot, no registration. Only when none
// answers is a passkey created, under a per-vault user identity that the
// header records (first enrolment), so a later explicit "create a new
// passkey" replaces the old one in the password manager rather than piling
// up; the slots of the replaced passkey are pruned then. Unlock lists the
// vault's own credential ids first (the password manager shows only those)
// and falls back to a discoverable assertion + slot lookup when the listed
// ids are gone (browsers report "no such passkey" and "cancelled" alike, so
// a genuine cancel can cost one extra prompt).

import {
  deriveAccountsKey,
  deriveKek,
  deriveStoreKey,
  fromBase64Url,
  generateDek,
  generateSessionKey,
  open,
  PRF_SALT_V1,
  type SealedBox,
  seal,
  toBase64Url,
  utf8,
} from "./crypto";
import { PasskeyCancelledError, type PrfAuthenticator } from "./passkey";
import type { VaultStorage } from "./storage";

export const VAULT_HEADER_KEY = "header";
export const VAULT_SESSION_KEY = "session";
export const VAULT_ACCOUNTS_KEY = "accounts";

export const SESSION_KEY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export const CURRENT_KEK_EPOCH = 1;

const AAD_SESSION = "lately-vault/session/v1";
const AAD_ACCOUNTS = "lately-vault/accounts/v1";
const slotAad = (credId: string, kekEpoch: number) => `lately-vault/slot/v1:${credId}:${kekEpoch}`;

export interface VaultSlot {
  // base64url credential id
  credId: string;
  kekEpoch: number;
  wrapped: SealedBox;
  createdAt: number;
}

// The WebAuthn user identity of the vault's passkey (rp lately.org + user.id
// is what the password manager keys "replace on re-register" on). Recorded
// at the first enrolment — from the discovered credential's userHandle when
// an existing passkey is reused, else derived from the account name — and
// reused by every later registration for this vault.
export interface VaultPasskeyUser {
  // base64url user.id (16 bytes)
  id: string;
  name: string;
  displayName: string;
}

export interface VaultHeader {
  version: 1;
  createdAt: number;
  slots: VaultSlot[];
  passkeyUser?: VaultPasskeyUser;
}

export interface VaultSession {
  sk: CryptoKey;
  wrapped: SealedBox;
  createdAt: number;
}

export type VaultProtection = "passkey" | "browser";
export type VaultUnlockedBy = "session" | "passkey" | "created";

export type VaultState =
  | { status: "empty" }
  | { status: "unlocked"; protection: VaultProtection; unlockedBy: VaultUnlockedBy }
  // A DEK exists but this call could not unwrap it. `unlockable` is true when
  // a passkey ceremony (interactive unlock) can; false means only reset() helps.
  | { status: "locked"; protection: VaultProtection; unlockable: boolean };

export type VaultErrorCode =
  | "locked"
  | "already-exists"
  | "prf-unsupported"
  | "unknown-passkey"
  | "corrupt"
  | "cancelled";

export class VaultError extends Error {
  constructor(
    readonly code: VaultErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "VaultError";
  }
}

export interface VaultOptions {
  rpId: string;
  rpName?: string;
  now?: () => number;
  sessionKeyMaxAgeMs?: number;
}

export interface EnrollPasskeyOptions {
  userName: string;
  userDisplayName?: string;
  // "reuse-or-create" (default): discoverable assertion first; register only
  // when no lately.org passkey answers. "create": register a new passkey
  // under the vault's user identity — it REPLACES the previous one in the
  // password manager, so the UI must say so.
  mode?: "reuse-or-create" | "create";
}

export interface EnrollPasskeyResult {
  // base64url credential id of the slot that now wraps the DEK
  credId: string;
  // true when an existing lately.org passkey answered and no passkey was
  // created; false when one was registered.
  reused: boolean;
}

// The roster the page seals under the DEK: origin + username + the secret the
// core needs, with renewal metadata for OAuth sessions.
export interface SealedAccount {
  origin: string;
  username: string;
  // OAuth sessions seal access and refresh tokens together.
  credentialKind: "basicPassword" | "oidcSession";
  secret: string;
}

export interface SealedAccountsPayload {
  version: 1;
  accounts: SealedAccount[];
  active: number;
}

// Default WebAuthn user.id for a vault's passkey when no existing passkey
// tells us its userHandle: derived from the account name, recorded in the
// header at first enrolment (VaultHeader.passkeyUser) and reused from there,
// so re-registering replaces the previous passkey in the password manager.
export async function passkeyUserId(userName: string): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    utf8(`lately.org/vault/user:${userName.toLowerCase()}`),
  );
  return new Uint8Array(digest).slice(0, 16);
}

export class Vault {
  private dek: Uint8Array | null = null;
  private readonly now: () => number;
  private readonly sessionKeyMaxAgeMs: number;
  readonly rpId: string;
  readonly rpName: string;

  constructor(
    private readonly storage: VaultStorage,
    private readonly authenticator: PrfAuthenticator,
    options: VaultOptions,
  ) {
    this.rpId = options.rpId;
    this.rpName = options.rpName ?? "Lately";
    this.now = options.now ?? (() => Date.now());
    this.sessionKeyMaxAgeMs = options.sessionKeyMaxAgeMs ?? SESSION_KEY_MAX_AGE_MS;
  }

  get isUnlocked(): boolean {
    return this.dek !== null;
  }

  // Whether this browser can run a passkey ceremony at all (PRF support is
  // only known after an assertion; enrollPasskey reports it).
  passkeyAvailable(): Promise<boolean> {
    return this.authenticator.available();
  }

  // Read-only view; never prompts.
  async status(): Promise<VaultState> {
    const header = await this.storage.get<VaultHeader>(VAULT_HEADER_KEY);
    if (!header) {
      return { status: "empty" };
    }
    const protection = protectionOf(header);
    if (this.dek) {
      return { status: "unlocked", protection, unlockedBy: "session" };
    }
    const session = await this.storage.get<VaultSession>(VAULT_SESSION_KEY);
    if (session) {
      return { status: "unlocked", protection, unlockedBy: "session" };
    }
    return {
      status: "locked",
      protection,
      unlockable: protection === "passkey" && (await this.authenticator.available()),
    };
  }

  // The boot path. `interactive: false` never talks to the authenticator (safe
  // to run on load); `interactive: true` runs the passkey ceremony when that
  // is what stands between us and the DEK (call it from a user gesture).
  async unlock(options: { interactive: boolean }): Promise<VaultState> {
    const header = await this.storage.get<VaultHeader>(VAULT_HEADER_KEY);
    if (!header) {
      this.dek = null;
      return { status: "empty" };
    }
    const protection = protectionOf(header);

    if (this.dek) {
      return { status: "unlocked", protection, unlockedBy: "session" };
    }

    const session = await this.storage.get<VaultSession>(VAULT_SESSION_KEY);
    if (session) {
      let dek: Uint8Array | null = null;
      try {
        dek = await open(session.sk, session.wrapped, AAD_SESSION);
      } catch {
        // A session record that no longer unwraps (partial eviction, a
        // different origin's leftovers): forget it and fall through.
        await this.storage.delete(VAULT_SESSION_KEY);
      }
      if (dek) {
        const expired = this.now() - session.createdAt > this.sessionKeyMaxAgeMs;
        if (!expired) {
          this.dek = dek;
          return { status: "unlocked", protection, unlockedBy: "session" };
        }
        if (protection === "browser") {
          // Nothing stronger to re-prompt for: rotate the SK silently.
          this.dek = dek;
          await this.mintSession(dek);
          return { status: "unlocked", protection, unlockedBy: "session" };
        }
        // Passkey-protected and the SK is due: require the ceremony. Keep
        // the old record until the ceremony succeeds so a cancelled prompt
        // does not lock the user out of an otherwise working vault.
        if (!options.interactive) {
          return { status: "locked", protection, unlockable: await this.authenticator.available() };
        }
      }
    }

    if (protection === "browser") {
      // No slots and no usable session: unrecoverable by design.
      return { status: "locked", protection, unlockable: false };
    }
    if (!options.interactive) {
      return { status: "locked", protection, unlockable: await this.authenticator.available() };
    }
    if (!(await this.authenticator.available())) {
      return { status: "locked", protection, unlockable: false };
    }

    const dek = await this.unwrapWithPasskey(header);
    this.dek = dek;
    await this.mintSession(dek);
    return { status: "unlocked", protection, unlockedBy: "passkey" };
  }

  // Fresh vault at first sign-in: DEK + SK, no passkey yet ("browser"
  // protection). enrollPasskey() upgrades it.
  async create(): Promise<VaultState> {
    if (await this.storage.get<VaultHeader>(VAULT_HEADER_KEY)) {
      throw new VaultError("already-exists", "The vault already exists; unlock or reset it.");
    }
    const dek = generateDek();
    const header: VaultHeader = { version: 1, createdAt: this.now(), slots: [] };
    await this.storage.put(VAULT_HEADER_KEY, header);
    this.dek = dek;
    await this.mintSession(dek);
    return { status: "unlocked", protection: "browser", unlockedBy: "created" };
  }

  // Adds a passkey slot: reuse-first (a discoverable assertion — whichever
  // lately.org passkey the password manager holds — evaluates the PRF and
  // its KEK wraps this vault's DEK), registering a new passkey only when
  // none answers or when `mode: "create"` asks for it. Requires an unlocked
  // vault and a user gesture.
  async enrollPasskey(options: EnrollPasskeyOptions): Promise<EnrollPasskeyResult> {
    const dek = this.requireDek();
    const header = await this.requireHeader();
    const mode = options.mode ?? "reuse-or-create";

    if (mode === "reuse-or-create") {
      let existing: Awaited<ReturnType<PrfAuthenticator["assert"]>> | null = null;
      try {
        existing = await this.authenticator.assert({
          rpId: this.rpId,
          salt: PRF_SALT_V1,
          allowCredentialIds: [],
        });
      } catch (cause) {
        // NotAllowedError is both "no passkey for lately.org here" and "the
        // user cancelled": either way, offer to create one (that prompt can
        // be cancelled too, and then we report the cancel).
        if (!(cause instanceof PasskeyCancelledError)) {
          throw translate(cause);
        }
      }
      if (existing) {
        if (!existing.prf) {
          throw new VaultError(
            "prf-unsupported",
            "This passkey provider does not support the PRF extension; the vault stays unlocked by this browser only.",
          );
        }
        const credId = toBase64Url(existing.credentialId);
        const passkeyUser: VaultPasskeyUser = header.passkeyUser ?? {
          id: toBase64Url(existing.userHandle ?? (await passkeyUserId(options.userName))),
          name: options.userName,
          displayName: options.userDisplayName ?? options.userName,
        };
        await this.addSlot(header, dek, credId, existing.prf, {
          passkeyUser,
          pruneOthers: false,
        });
        return { credId, reused: true };
      }
    }

    // Register under the vault's stable identity: same rp + user.id makes the
    // password manager replace the previous passkey, so the old slots are
    // pruned (their credential is gone; nothing can unwrap them any more).
    const passkeyUser: VaultPasskeyUser = header.passkeyUser ?? {
      id: toBase64Url(await passkeyUserId(options.userName)),
      name: options.userName,
      displayName: options.userDisplayName ?? options.userName,
    };
    let registration: Awaited<ReturnType<PrfAuthenticator["register"]>>;
    try {
      registration = await this.authenticator.register({
        rpId: this.rpId,
        rpName: this.rpName,
        userId: fromBase64Url(passkeyUser.id),
        userName: passkeyUser.name,
        userDisplayName: passkeyUser.displayName,
      });
    } catch (cause) {
      throw translate(cause);
    }
    let assertion: Awaited<ReturnType<PrfAuthenticator["assert"]>>;
    try {
      assertion = await this.authenticator.assert({
        rpId: this.rpId,
        salt: PRF_SALT_V1,
        allowCredentialIds: [registration.credentialId],
      });
    } catch (cause) {
      throw translate(cause);
    }
    if (!assertion.prf) {
      throw new VaultError(
        "prf-unsupported",
        "This passkey provider does not support the PRF extension; the vault stays unlocked by this browser only.",
      );
    }
    const credId = toBase64Url(assertion.credentialId);
    await this.addSlot(header, dek, credId, assertion.prf, { passkeyUser, pruneOthers: true });
    return { credId, reused: false };
  }

  // Forget the session key: the next boot needs the passkey (or, without
  // one, a reset). The DEK leaves memory too; the caller closes the shell.
  async lock(): Promise<void> {
    this.dek = null;
    await this.storage.delete(VAULT_SESSION_KEY);
  }

  // Wipe every vault record (sign-out of the last account, "forget this
  // browser", or an unrecoverable vault). The passkey itself stays in the
  // password manager; the next enrolment finds and reuses it.
  async reset(): Promise<void> {
    this.dek = null;
    await this.storage.clear();
  }

  // The SQLite codec key for the core (HKDF subkey of the DEK), base64url.
  async storeKey(): Promise<string> {
    return toBase64Url(await deriveStoreKey(this.requireDek()));
  }

  async sealAccounts(payload: SealedAccountsPayload): Promise<void> {
    const key = await deriveAccountsKey(this.requireDek());
    const box = await seal(key, utf8(JSON.stringify(payload)), AAD_ACCOUNTS);
    await this.storage.put(VAULT_ACCOUNTS_KEY, box);
  }

  async openAccounts(): Promise<SealedAccountsPayload | null> {
    const box = await this.storage.get<SealedBox>(VAULT_ACCOUNTS_KEY);
    if (!box) {
      return null;
    }
    const key = await deriveAccountsKey(this.requireDek());
    let bytes: Uint8Array;
    try {
      bytes = await open(key, box, AAD_ACCOUNTS);
    } catch {
      throw new VaultError("corrupt", "The sealed account list could not be opened.");
    }
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as SealedAccountsPayload;
    if (parsed?.version !== 1 || !Array.isArray(parsed.accounts)) {
      throw new VaultError("corrupt", "The sealed account list has an unknown format.");
    }
    return parsed;
  }

  async clearAccounts(): Promise<void> {
    await this.storage.delete(VAULT_ACCOUNTS_KEY);
  }

  // Test/diagnostic hook: the header without any secret material.
  async header(): Promise<VaultHeader | null> {
    return (await this.storage.get<VaultHeader>(VAULT_HEADER_KEY)) ?? null;
  }

  private requireDek(): Uint8Array {
    if (!this.dek) {
      throw new VaultError("locked", "The vault is locked.");
    }
    return this.dek;
  }

  private async requireHeader(): Promise<VaultHeader> {
    const header = await this.storage.get<VaultHeader>(VAULT_HEADER_KEY);
    if (!header) {
      throw new VaultError("locked", "The vault does not exist.");
    }
    return header;
  }

  private async addSlot(
    header: VaultHeader,
    dek: Uint8Array,
    credId: string,
    prf: Uint8Array,
    options: { passkeyUser: VaultPasskeyUser; pruneOthers: boolean },
  ): Promise<void> {
    const kek = await deriveKek(prf);
    const wrapped = await seal(kek, dek, slotAad(credId, CURRENT_KEK_EPOCH));
    const slots = options.pruneOthers ? [] : header.slots.filter((slot) => slot.credId !== credId);
    slots.push({ credId, kekEpoch: CURRENT_KEK_EPOCH, wrapped, createdAt: this.now() });
    await this.storage.put(VAULT_HEADER_KEY, {
      ...header,
      slots,
      passkeyUser: options.passkeyUser,
    } satisfies VaultHeader);
  }

  private async mintSession(dek: Uint8Array): Promise<void> {
    const sk = await generateSessionKey();
    const wrapped = await seal(sk, dek, AAD_SESSION);
    const session: VaultSession = { sk, wrapped, createdAt: this.now() };
    await this.storage.put(VAULT_SESSION_KEY, session);
  }

  private async unwrapWithPasskey(header: VaultHeader): Promise<Uint8Array> {
    let assertion: Awaited<ReturnType<PrfAuthenticator["assert"]>>;
    try {
      assertion = await this.authenticator.assert({
        rpId: this.rpId,
        salt: PRF_SALT_V1,
        allowCredentialIds: header.slots.map((slot) => fromBase64Url(slot.credId)),
      });
    } catch (cause) {
      if (!(cause instanceof PasskeyCancelledError)) {
        throw translate(cause);
      }
      // The listed credentials may no longer exist (replaced on another
      // surface, removed from the password manager) — browsers report that
      // exactly like a cancel. One discoverable attempt tells the two apart:
      // an enrolled passkey answering means the listed attempt failed for a
      // spurious reason; a stranger means the slots are stale (unknown-passkey,
      // and the UI offers to sign in again); a second cancel is a cancel.
      try {
        assertion = await this.authenticator.assert({
          rpId: this.rpId,
          salt: PRF_SALT_V1,
          allowCredentialIds: [],
        });
      } catch (fallbackCause) {
        throw translate(fallbackCause);
      }
    }
    if (!assertion.prf) {
      throw new VaultError(
        "prf-unsupported",
        "This browser did not evaluate the passkey PRF extension.",
      );
    }
    const credId = toBase64Url(assertion.credentialId);
    const slot = header.slots.find((candidate) => candidate.credId === credId);
    if (!slot) {
      throw new VaultError(
        "unknown-passkey",
        "That passkey is not enrolled in this vault (it may have been replaced on another device). Sign in again to enrol it.",
      );
    }
    const kek = await deriveKek(assertion.prf);
    try {
      return await open(kek, slot.wrapped, slotAad(slot.credId, slot.kekEpoch));
    } catch {
      throw new VaultError(
        "corrupt",
        "The passkey no longer unwraps this vault (rotated or corrupt slot).",
      );
    }
  }
}

function protectionOf(header: VaultHeader): VaultProtection {
  return header.slots.length > 0 ? "passkey" : "browser";
}

function translate(cause: unknown): Error {
  if (cause instanceof PasskeyCancelledError) {
    return new VaultError("cancelled", cause.message);
  }
  return cause instanceof Error ? cause : new Error(String(cause));
}
